import React, { memo, useMemo } from 'react';
import { useWindowDimensions } from 'react-native';

import { EventInfo } from '@/api/events/types/types';
import { Box, Row } from '@/components/lib/components';
import { allPlayerPropsSelector, usePlayerPropsStore } from '@/feature/betslip-pickem/hooks/use-player-props-store';
import { PlayerWithTeam, PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { Section as ListSection } from '@/feature/lobby/utils/players';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { useResponsiveColumnCount } from '@/feature/responsive-design/hooks/use-responsive-column-count';
import { MAX_WEB_WIDTH } from '@/utils/constants-platform-specific';

import { PickSelectionMethods } from '../../hooks/use-pick-selection';
import { checkIfPromoProjection } from '../../utils/lineupsUtils';
import { PlayerTile } from './PlayerTile';

const TileSpacings = {
    paddingVertical: 6,
    paddingHorizontal: 16,
    gap: 12,
};

const TILE_MIN_WIDTH = 158;

/**
 * Component used to display the number of projections that are on a row in section list.
 * Since there is no prop for section list to change the number of elements on a row, we are implementing a custom method.
 *
 * For example if we want to add 2 elements on a row. We go through the list of projections in section list and add the current element
 * and the next element (since we want 2 on a row) and display both at the current step. Since our next element has already been displayed
 * in the previous step, on the next step we are going to skip him.
 * @param props
 * @returns
 */
export const PlayerTileRow = ({
    index,
    sortedPlayers,
    makeSelection,
    removeSelection,
    analyticsTag,
    playerProjectionFilter,
    hideGameTime,
    openPlayerProfile,
}: {
    index: number;
    section?: ListSection;
    sortedPlayers: PlayerWithTeamAndEvent[];
    analyticsTag?: string;
    playerProjectionFilter?: string;
    hideGameTime?: boolean;
    openPlayerProfile: (playerId: string, eventId: string) => void;
} & PickSelectionMethods) => {
    const columnsCount = useResponsiveColumnCount([1, 2, 3, 4, 5]);
    const isFirst = index === 0;

    //Array used to hold the number of elements that are on a row.
    const items = useMemo(() => {
        const projectionList = [];
        // We are going to add the current element and future elements (until we get the number of desired elements on a row)
        // to the items list and display them on a single row.
        let currentIndex = index;
        while (projectionList.length < columnsCount && currentIndex < sortedPlayers.length) {
            const currentItem = sortedPlayers[currentIndex];
            const { event: eventInfo, ...playerWithoutEvent } = currentItem;

            projectionList.push(
                <PlayerTileItem
                    eventInfo={eventInfo}
                    player={playerWithoutEvent}
                    key={`player-${playerWithoutEvent.id}-${playerProjectionFilter}-${eventInfo.id}-${playerWithoutEvent.projections[0]?.type}--${playerWithoutEvent.projections[0]?.key}`}
                    testID={`player-${sortedPlayers.indexOf(currentItem).toString()}`}
                    openPlayerProfile={openPlayerProfile}
                    makeSelection={makeSelection}
                    removeSelection={removeSelection}
                    analyticsTag={analyticsTag}
                    hideGameTime={hideGameTime}
                />
            );

            currentIndex++;
        }

        return projectionList;
    }, [
        index,
        columnsCount,
        sortedPlayers,
        playerProjectionFilter,
        makeSelection,
        removeSelection,
        analyticsTag,
        hideGameTime,
        openPlayerProfile,
    ]);

    // This element has been already added in a previous step. We don't want to add duplicates so we are going to skip them.
    if (index % columnsCount !== 0) {
        return null;
    }

    return (
        <MaxWidthWrapper>
            <Row style={TileSpacings} marginTop={isFirst ? 's2' : 's0'}>
                {items}
            </Row>
        </MaxWidthWrapper>
    );
};

type PlayerTileItemProps = {
    eventInfo: EventInfo;
    player: PlayerWithTeam;
    testID?: string;
    analyticsTag?: string;
    hideGameTime?: boolean;
    openPlayerProfile: (playerId: string, eventId: string) => void;
} & PickSelectionMethods;

export const ESTIMATED_TILE_ROW_HEIGHT = 247;

export const PlayerTileItem = memo(
    ({
        eventInfo,
        player,
        testID,
        analyticsTag,
        removeSelection,
        makeSelection,
        hideGameTime,
        openPlayerProfile,
    }: PlayerTileItemProps) => {
        const columnsCount = useResponsiveColumnCount([1, 2, 3, 4, 5]);
        //we check for the number of cards that we will display the maximum width that one card can have to make all fit the entire screen
        const { width } = useWindowDimensions();
        const screenWidth = width > MAX_WEB_WIDTH ? MAX_WEB_WIDTH : width;
        const maxWidthAfterSpacings =
            screenWidth - 2 * TileSpacings.paddingHorizontal - TileSpacings.gap * (columnsCount - 1); // we subtract the paddings and the gaps between column
        const tileMaxWidth = maxWidthAfterSpacings / columnsCount;

        const allProjections = usePlayerPropsStore(allPlayerPropsSelector(player.id, eventInfo.id));

        const filteredProjections = useMemo(() => {
            //player.projections has the split projections(nuke|special|anchor| ...rest)
            const tileType = player.projections[0]?.type;
            const projectionKey = player.projections[0]?.key;
            //determine if this tile shows a promo(specials) projection or alternate projections
            const promoTile = checkIfPromoProjection(tileType);
            if (promoTile) {
                // only show specials (nuke | anchor | special)
                // also filter by projection.key, to ensure the PlayerTile will show the intended projection ( Passing YDS | Passing TD | etc )
                // when rendering multiple tiles for the same player with same projection type but different projection keys, on the "Popular" tab
                return allProjections.filter(p => p.type === tileType && p.key === projectionKey);
            } else {
                //show all alternative projections
                return allProjections.filter(p => !checkIfPromoProjection(p.type));
            }
        }, [allProjections, player.projections]);

        return (
            <Box
                flex={1}
                testID={`listedPlayer-${testID}`}
                style={{ minWidth: TILE_MIN_WIDTH, maxWidth: tileMaxWidth }}
                key={`player-${player.id}-${eventInfo.id}`}
            >
                <PlayerTile
                    player={player}
                    event={eventInfo}
                    testID={testID}
                    openPlayerProfile={openPlayerProfile}
                    removeSelection={removeSelection}
                    makeSelection={makeSelection}
                    analyticsTag={analyticsTag}
                    containerWidth={Math.max(TILE_MIN_WIDTH, tileMaxWidth)}
                    playerProjections={filteredProjections}
                    hideGameTime={hideGameTime}
                />
            </Box>
        );
    }
);
